import { useState, useEffect, useCallback, useMemo } from 'react';
import { GrammarCardService } from '../services/GrammarCardService';
import { GrammarCard, FilterOptions, CardCategory, GradeLevel, StudySession } from '../types/grammar';

export const useGrammarCards = () => {
  const [cardService] = useState(() => new GrammarCardService());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastReload, setLastReload] = useState<number>(0);

  // Initialize service
  useEffect(() => {
    const initializeService = async () => {
      try {
        setLoading(true);
        setError(null);
        await cardService.loadDatabase();
        setLastReload(Date.now());
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load cards';
        setError(errorMessage);
        console.error('Error loading grammar cards:', err);
      } finally {
        setLoading(false);
      }
    };

    initializeService();
  }, [cardService]);

  // Memoized methods to prevent unnecessary re-renders
  const getCardsByGradeLevel = useCallback((gradeLevel: number): GrammarCard[] => {
    return cardService.getCardsByGradeLevel(gradeLevel);
  }, [cardService, lastReload]);

  const getCardsByCategory = useCallback((categoryId: string): GrammarCard[] => {
    return cardService.getCardsByCategory(categoryId);
  }, [cardService, lastReload]);

  const filterCards = useCallback((options: FilterOptions): GrammarCard[] => {
    return cardService.filterCards(options);
  }, [cardService, lastReload]);

  const getCategories = useCallback((): CardCategory[] => {
    return cardService.getCategories();
  }, [cardService, lastReload]);

  const getGradeLevels = useCallback((): GradeLevel[] => {
    return cardService.getGradeLevels();
  }, [cardService, lastReload]);

  const getCategoriesForGrade = useCallback((gradeLevel: number): CardCategory[] => {
    return cardService.getCategoriesForGrade(gradeLevel);
  }, [cardService, lastReload]);

  const getRandomCards = useCallback((count: number, options?: FilterOptions): GrammarCard[] => {
    return cardService.getRandomCards(count, options);
  }, [cardService, lastReload]);

  const searchCards = useCallback((query: string): GrammarCard[] => {
    return cardService.searchCards(query);
  }, [cardService, lastReload]);

  const getRecommendedCards = useCallback((userLevel: number, weakCategories: string[] = [], count: number = 10): GrammarCard[] => {
    return cardService.getRecommendedCards(userLevel, weakCategories, count);
  }, [cardService, lastReload]);

  const updateCardStatistics = useCallback((cardId: string, isCorrect: boolean, responseTime: number): void => {
    cardService.updateCardStatistics(cardId, isCorrect, responseTime);
  }, [cardService]);

  // Memoized analytics
  const overallStats = useMemo(() => {
    return cardService.getOverallStats();
  }, [cardService, lastReload]);

  const categoryStats = useMemo(() => {
    return cardService.getCategoryStats();
  }, [cardService, lastReload]);

  // Study session management
  const createStudySession = useCallback((gradeLevel: number, categories: string[]): StudySession => {
    return cardService.createStudySession(gradeLevel, categories);
  }, [cardService]);

  const updateStudySession = useCallback((session: StudySession, cardId: string, isCorrect: boolean, responseTime: number): void => {
    cardService.updateStudySession(session, cardId, isCorrect, responseTime);
  }, [cardService]);

  const endStudySession = useCallback((session: StudySession): StudySession => {
    return cardService.endStudySession(session);
  }, [cardService]);

  // Utility functions
  const reloadDatabase = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      cardService.clearCache();
      await cardService.loadDatabase();
      setLastReload(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reload cards');
    } finally {
      setLoading(false);
    }
  }, [cardService]);

  const exportToCSV = useCallback((): string => {
    return cardService.exportCardsToCSV();
  }, [cardService]);

  return {
    // Service instance
    cardService,
    
    // State
    loading,
    error,
    isReady: cardService.isReady(),
    
    // Core methods
    getCardsByGradeLevel,
    getCardsByCategory,
    filterCards,
    getCategories,
    getGradeLevels,
    getCategoriesForGrade,
    getRandomCards,
    searchCards,
    getRecommendedCards,
    
    // Statistics
    updateCardStatistics,
    overallStats,
    categoryStats,
    
    // Study sessions
    createStudySession,
    updateStudySession,
    endStudySession,
    
    // Utilities
    reloadDatabase,
    exportToCSV
  };
};